import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { myCourses, type MyCourseSummary } from "../api/assignments";
import { ApiError } from "../api/client";
import { fileUrl } from "../api/files";
import { formatMoscowDate } from "../utils/datetime";

export function MyCoursesSection() {
  const [items, setItems] = useState<MyCourseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void myCourses()
      .then((r) => setItems(r.items))
      .catch((err) => {
        setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
        setItems([]);
      });
  }, []);

  if (items === null) {
    return (
      <section className="mt-6 rounded-lg bg-white p-4 text-sm text-slate-400 shadow-sm">
        Загружаем твои курсы…
      </section>
    );
  }
  if (items.length === 0 && !error) {
    return null; // не показываем секцию ученикам без назначений
  }

  return (
    <section className="mt-6">
      <h2 className="mb-3 text-lg font-semibold text-slate-900">Мои курсы</h2>
      {error && (
        <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      <ul className="space-y-2">
        {items.map((c) => (
          <li key={c.course_id}>
            <Link
              to={`/learn/courses/${c.course_id}`}
              className="group card relative block overflow-hidden p-0 transition hover:shadow-md"
            >
              {c.cover_file_id ? (
                <div className="relative aspect-[21/9] w-full">
                  <img
                    src={fileUrl(c.cover_file_id)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold drop-shadow-sm">{c.course_title}</span>
                      {c.is_mandatory && <span className="badge bg-white/20 text-white">Обязательный</span>}
                      {c.overdue && <span className="badge bg-red-500/90 text-white">Просрочен</span>}
                    </div>
                    <div className="mt-1 text-xs text-white/80">
                      {c.lessons_completed} из {c.lessons_total} уроков
                      {c.due_at && <span> · до {formatMoscowDate(c.due_at)}</span>}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/25">
                        <div
                          className={`h-full transition-all ${c.percent === 100 ? "bg-brand-success" : "bg-white"}`}
                          style={{ width: `${c.percent}%` }}
                        />
                      </div>
                      <span className="shrink-0 text-xs font-semibold">{c.percent}%</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-ink group-hover:text-brand">
                          {c.course_title}
                        </span>
                        {c.is_mandatory && <span className="badge badge-brand">Обязательный</span>}
                        {c.overdue && <span className="badge badge-danger">Просрочен</span>}
                      </div>
                      <div className="mt-1 text-xs text-ink-muted">
                        {c.lessons_completed} из {c.lessons_total} уроков
                        {c.due_at && (
                          <span> · до {formatMoscowDate(c.due_at)}</span>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex w-32 flex-col items-end">
                      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-alt">
                        <div
                          className={`h-full rounded-full transition-all ${
                            c.percent === 100 ? "bg-brand-success" : "bg-brand"
                          }`}
                          style={{ width: `${c.percent}%` }}
                        />
                      </div>
                      <div className="mt-1 text-xs text-ink-muted">{c.percent}%</div>
                    </div>
                  </div>
                </div>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
